import { useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import { GLOBE_RADIUS, latLonToVector3 } from "../lib/seismic";
import { useSim } from "../lib/store";

// Renders a single epicenter pin. Either a committed `event` (interactive, with
// on-globe menu) or a `draft` gold placeholder.
export function EpicenterPin({ event, draft }) {
  const src = event || draft;
  const gold = !!draft;
  const ringRef = useRef();
  const ring2Ref = useRef();
  const lightRef = useRef();

  const selectedId = useSim((s) => s.selectedId);
  const pinMenuId = useSim((s) => s.pinMenuId);
  const setPinMenuId = useSim((s) => s.setPinMenuId);
  const setSelectedId = useSim((s) => s.setSelectedId);
  const setFocusEvent = useSim((s) => s.setFocusEvent);
  const removeEvent = useSim((s) => s.removeEvent);
  const updateEvent = useSim((s) => s.updateEvent);

  const isSelected = event && selectedId === event.id;
  const menuOpen = event && pinMenuId === event.id;

  const { position, quaternion } = useMemo(() => {
    if (!src) return { position: new THREE.Vector3(), quaternion: new THREE.Quaternion() };
    const pos = latLonToVector3(src.lat, src.lon, GLOBE_RADIUS);
    const normal = pos.clone().normalize();
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), normal);
    return { position: pos, quaternion: q };
  }, [src]);

  useFrame((state) => {
    const st = useSim.getState();
    const t = state.clock.elapsedTime;
    const pulse = (r, speed, offset) => {
      if (!r) return;
      const s = 1 + ((t * speed + offset) % 1) * 2.4;
      r.scale.setScalar(s);
      r.material.opacity = Math.max(0, 0.6 - ((t * speed + offset) % 1) * 0.6);
    };
    pulse(ringRef.current, 0.6, 0);
    pulse(ring2Ref.current, 0.6, 0.5);

    if (lightRef.current) {
      const elapsed = (performance.now() - st.simStart) / 1000;
      const running = event && st.simulating && elapsed <= event.duration + 0.5;
      const p = running ? Math.min(elapsed / event.duration, 1) : 0;
      const envelope = running ? Math.sin(p * Math.PI) * 0.7 + 0.3 : 0;
      const mag = event ? event.magnitude : 6;
      const magFactor = THREE.MathUtils.mapLinear(mag, 4, 9.5, 3, 16);
      lightRef.current.intensity = running ? magFactor * envelope : 0.0;
    }
  });

  if (!src) return null;

  const markerColor = gold ? "#f5c518" : isSelected ? "#ff6644" : "#ff4444";
  const markerEmissive = gold ? "#e0a800" : isSelected ? "#ff4422" : "#ff2a2a";

  const onPinDown = (e) => {
    if (!event) return;
    e.stopPropagation();
    const st = useSim.getState();
    if (st.simulating) return;
    setSelectedId(event.id);
    setPinMenuId(menuOpen ? null : event.id);
  };

  return (
    <group position={position} quaternion={quaternion}>
      <pointLight ref={lightRef} color="#ff3b30" distance={4.5} decay={2} intensity={0} position={[0, 0.15, 0]} />

      <mesh position={[0, 0.12, 0]} onPointerDown={onPinDown}>
        <coneGeometry args={[0.03, 0.26, 16]} />
        <meshStandardMaterial color={markerColor} emissive={markerEmissive} emissiveIntensity={2.4} toneMapped={false} />
      </mesh>
      <mesh position={[0, 0.005, 0]} onPointerDown={onPinDown}>
        <sphereGeometry args={[0.032, 16, 16]} />
        <meshStandardMaterial color="#ffffff" emissive={gold ? "#ffe08a" : "#ff5555"} emissiveIntensity={3} toneMapped={false} />
      </mesh>

      {isSelected && !gold && (
        <mesh position={[0, 0.002, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.07, 0.085, 40]} />
          <meshBasicMaterial color="#ffd94a" transparent opacity={0.9} side={THREE.DoubleSide} depthWrite={false} toneMapped={false} />
        </mesh>
      )}

      <mesh ref={ringRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.002, 0]}>
        <ringGeometry args={[0.05, 0.065, 48]} />
        <meshBasicMaterial color={gold ? "#f5c518" : "#ff5555"} transparent opacity={0.6} side={THREE.DoubleSide} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
      </mesh>
      <mesh ref={ring2Ref} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.002, 0]}>
        <ringGeometry args={[0.05, 0.065, 48]} />
        <meshBasicMaterial color={gold ? "#ffd94a" : "#ff8855"} transparent opacity={0.5} side={THREE.DoubleSide} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
      </mesh>

      {menuOpen && (
        <Html position={[0, 0.5, 0]} center distanceFactor={6} zIndexRange={[50, 0]}>
          <div
            data-testid={`pin-menu-${event.id}`}
            className="min-w-[132px] select-none rounded-xl bg-zinc-950/95 border border-white/20 backdrop-blur-xl shadow-[0_10px_30px_rgba(0,0,0,0.8)] p-2"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-1.5 px-1">
              <span className="font-mono-tele text-[10px] uppercase tracking-wider text-red-400">
                Mw {event.magnitude.toFixed(1)}
              </span>
              <button
                data-testid={`pin-menu-close-${event.id}`}
                onClick={() => setPinMenuId(null)}
                className="text-zinc-500 hover:text-white text-xs leading-none"
              >
                ✕
              </button>
            </div>
            <div className="flex items-center gap-1 mb-1.5">
              <button
                data-testid={`pin-mag-down-${event.id}`}
                onClick={() => updateEvent(event.id, { magnitude: Math.max(3, +(event.magnitude - 0.5).toFixed(1)) })}
                className="flex-1 py-1 rounded-md bg-white/5 hover:bg-white/10 text-zinc-200 text-sm font-bold"
              >
                −
              </button>
              <button
                data-testid={`pin-mag-up-${event.id}`}
                onClick={() => updateEvent(event.id, { magnitude: Math.min(9.5, +(event.magnitude + 0.5).toFixed(1)) })}
                className="flex-1 py-1 rounded-md bg-white/5 hover:bg-white/10 text-zinc-200 text-sm font-bold"
              >
                +
              </button>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid={`pin-focus-${event.id}`}
                onClick={() => setFocusEvent(event.id)}
                className="flex-1 py-1 rounded-md bg-red-600/80 hover:bg-red-500 text-white text-[10px] font-semibold uppercase tracking-wide"
              >
                1:1
              </button>
              <button
                data-testid={`pin-delete-${event.id}`}
                onClick={() => removeEvent(event.id)}
                className="flex-1 py-1 rounded-md bg-white/5 hover:bg-red-950/60 text-red-300 text-[10px] font-semibold uppercase tracking-wide"
              >
                Del
              </button>
            </div>
          </div>
        </Html>
      )}
    </group>
  );
}
